import { Switch } from '@thanx/uikit/switch'
import { useStyletron } from '@thanx/uikit/theme'
import { StyledDropdownListItem } from 'baseui/select/styled-components'
import isNil from 'lodash/isNil'

import { buildTranslate } from 'locales'
import DropdownPaginationButton from './DropdownPaginationButton'

import type { Option } from 'baseui/select'
import type { PaginationT } from 'models/Pagination'

type CustomControlsProps = {
  pagination?: PaginationT
  onPageChange?: (page: number) => void
  selectAll: boolean
  isLoading: boolean
  value: Option[]
  onSelectAllChange: (selected: boolean) => void
}

const t = buildTranslate('redeem_manager.modifier_select')

const CustomControls = ({
  value,
  pagination,
  onPageChange,
  selectAll,
  onSelectAllChange,
  isLoading = false,
}: CustomControlsProps) => {
  const [css, defaultTheme] = useStyletron()

  const page = pagination?.current_page || 1
  const pageSize = pagination?.per_page || 1
  const total = pagination?.total_count || 0
  const totalPages = pagination?.total_pages || 1
  const start = (page - 1) * pageSize + 1
  const end = Math.min(page * pageSize, total)

  const shouldDisplayPagination =
    !isLoading && !isNil(pagination) && !isNil(onPageChange)

  const handlePageChange = (direction: 'next' | 'prev') => () => {
    const newPage = direction === 'next' ? page + 1 : page - 1
    onPageChange?.(newPage)
  }

  return (
    <StyledDropdownListItem
      style={{
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        borderBottom: '1px solid #E0E0E0',
      }}
    >
      <div className="d-flex align-items-center">
        <input
          type="checkbox"
          checked={selectAll}
          onChange={() => onSelectAllChange(!selectAll)}
          className="m-0"
          id="select-all-checkbox"
        />
        <label
          htmlFor="select-all-checkbox"
          className={css({
            ...defaultTheme.typography.font200,
            color: defaultTheme.colors.grey70,
            fontSize: defaultTheme.sizing.scale550,
            margin: '0 30px 0 8px',
          })}
        >
          {t('select_all')}
        </label>
      </div>
      <span
        className={`${css({
          color: defaultTheme.colors.grey50,
          fontWeight: 400,
          fontSize: '13px',
          marginRight: defaultTheme.sizing.scale550,
        })}`}
      >
        {t('count_selected', { count: value.length })}
      </span>
      <Switch condition={shouldDisplayPagination}>
        <span
          className={`d-flex align-items-center ${css({
            color: defaultTheme.colors.grey50,
            fontWeight: 400,
            fontSize: '13px',
            gap: '8px',
            marginLeft: 'auto',
          })}`}
        >
          {totalPages <= 1 ? (
            <span>{t('count_results', { count: total })}</span>
          ) : (
            <div
              className={`d-flex align-items-center ${css({
                gap: '8px',
                marginLeft: 'auto',
                marginRight: '8px',
              })}`}
            >
              <span>{t('start_end_of_total', { start, end, total })}</span>
              <DropdownPaginationButton
                direction="left"
                disabled={page <= 1}
                onClick={handlePageChange('prev')}
                aria-label={t('previous_page')}
              />
              <DropdownPaginationButton
                direction="right"
                disabled={page >= totalPages}
                onClick={handlePageChange('next')}
                aria-label={t('next_page')}
              />
            </div>
          )}
        </span>
      </Switch>
    </StyledDropdownListItem>
  )
}

export default CustomControls
